import { FileSearch, Landmark, ScrollText, ScanSearch, ShieldCheck, History, Scale, Building2, PiggyBank, TrendingUp, Landmark as Gov, Home } from "lucide-react";

export const CONTACT = {
  phone: "+91 84249 33337",
  phoneRaw: "8424933337",
  email: "terraaandtitle@gmail.com",
  address: "W25, Lodha Amara, Kolshet Road, Thane, Maharashtra 400607",
  mapQuery: "Lodha Amara, Kolshet Road, Thane 400607",
};

export const CHALLENGES = [
  { title: "One missing paper can stall everything", text: "Documents may look fine until you transact. A single missing link or mismatched name can freeze a sale, loan, or inheritance for months.", cta: "Fix my records" },
  { title: "Hidden claims grow silently", text: "Undisclosed mortgages, old liens, and unregistered family claims sit quietly in the records — until they surface as disputes.", cta: "Check my title" },
  { title: "Records are hard to read", text: "7/12 extracts, mutation entries, and legacy vernacular deeds are difficult to interpret without trained eyes.", cta: "Get expert help" },
  { title: "Away from your land, worry grows", text: "NRIs and out-of-city owners can't watch boundaries, tenants, or filings. Small issues become expensive ones.", cta: "Talk to an expert" },
];

export const YOU_PROVIDE = [
  ["Property address", "Or survey / plot / CTS number — even partial details work"],
  ["Any documents you hold", "Sale deed, 7/12, EC, agreements — upload what you have"],
  ["Your purpose", "Purchase, loan, litigation, inheritance, or peace of mind"],
];

export const YOU_RECEIVE = [
  ["Source-verified records", "Checked directly on MahaBhulekh, IGR e-Search & Digital Satbara"],
  ["Terraa Score /1000", "A weighted rating across six risk categories"],
  ["Red flags, stated plainly", "Every defect documented with the underlying record"],
  ["Decision-ready PDF report", "Formatted for legal opinions, credit files & boards"],
  ["Expert walkthrough", "A case manager explains every finding to you"],
];

export const DUE_DILIGENCE_ITEMS = [
  { id: "mother_deed", title: "Mother Deed Verification", mandatory: true, tip: "Trace the ownership history for the last 30 years — an unbroken Chain of Title is the single strongest proof that the seller truly owns what they're selling." },
  { id: "ec", title: "Encumbrance Certificate (EC)", mandatory: true, tip: "Check 15–30 years of registered claims — a clean EC confirms no hidden mortgages, liens, or charges are riding on the property." },
  { id: "na_order", title: "NA (Non-Agricultural) Order", mandatory: false, tip: "For agricultural land used for housing, the official conversion order is what makes construction legal — without it, structures can be demolished." },
  { id: "extract_712", title: "7/12 Extract / Record of Rights", mandatory: false, tip: "Verify the current owner's name and 'other rights' column — this is where tenancies, loans, and liabilities quietly appear." },
  { id: "tax_receipts", title: "Tax Receipts", mandatory: false, tip: "Up-to-date property tax receipts prove active, undisputed possession — missing receipts are a major red flag in Terraa Scores." },
  { id: "layout_plan", title: "Approved Layout Plan", mandatory: false, tip: "Confirm the plot matches the sanctioned layout — unapproved plots can be denied building permission or utility connections." },
  { id: "boundary_survey", title: "Physical Boundary Survey", mandatory: false, tip: "Match on-ground dimensions with the official survey map — encroachments and boundary shortfalls only show up on site." },
  { id: "village_records", title: "Patwari / Village Records Verification", mandatory: false, tip: "A manual check at the local village office surfaces disputes and pending mutations that online records haven't caught up with." },
  { id: "poa", title: "Power of Attorney (PoA)", mandatory: false, tip: "If the seller isn't the owner, the PoA must be registered and specifically authorise sale — unregistered PoAs won't survive a challenge." },
  { id: "commencement_cert", title: "Commencement Certificate", mandatory: false, tip: "For under-construction plots, this certificate proves the builder has legal sanction to begin work — buying without it transfers the risk to you." },
];

export const WHAT_WE_CHECK = [
  "Ownership and title chain signals",
  "Encumbrance indicators",
  "Zoning and land-use issues",
  "Tax and document compliance",
  "Public record matching",
  "Missing critical documents",
  "High-risk inconsistencies",
  "Buy / caution / high-risk summary",
];

export const RATING_BANDS = [
  { range: "80–100", label: "Lower visible risk", color: "#1D504C" },
  { range: "60–79", label: "Needs follow-up", color: "#C1A74A" },
  { range: "30–59", label: "Significant caution", color: "#b45309" },
  { range: "Below 30", label: "Major red flags", color: "#b91c1c" },
];

export const WHO_ITS_FOR = [
  { title: "Individual Buyers", text: "Buying a plot, land parcel, or second home in Maharashtra? Understand the risk before you sign or pay a token." },
  { title: "Real Estate Agents", text: "Close faster with verified listings. A Land Rating turns a hesitant buyer into a confident one — and protects your reputation." },
  { title: "Lawyers & Advisors", text: "Use our structured reports as a diligence starting point across multiple client matters, with records already assembled." },
];

export const PRICING_PLANS = [
  { id: "basic", name: "Basic Land Check", price: "₹2,999", period: "per property", description: "Best for initial screening",
    includes: ["Land Rating out of 100", "Category breakdown (title, encumbrance, zoning, tax)", "Deal-breaker alerts", "Rating summary PDF"],
    turnaround: "3–5 working days", cta: "buy" },
  { id: "standard", name: "Standard Verification Report", price: "₹5,999", period: "per property", description: "Best for most buyers", highlight: true,
    includes: ["Everything in Basic", "Full expert findings on every factor", "Government record verification table", "Examiner's notes & red flags", "Court & bank ready full PDF"],
    turnaround: "5–7 working days", cta: "buy" },
  { id: "priority", name: "Priority / Complex Review", price: "From ₹9,999", period: "custom scope", description: "Best for high-value or higher-risk cases",
    includes: ["Everything in Standard", "Priority turnaround", "Complex chains, litigation & multi-parcel cases", "Direct consultation with our experts"],
    turnaround: "Scoped per case", cta: "contact" },
];

export const VERIFICATION_FAQS = [
  { q: "What does Terraa & Title verify?", a: "We review submitted property documents and relevant available records to help identify ownership, encumbrance, zoning, tax, and document consistency issues — summarized in a Land Rating out of 100 and a structured PDF report." },
  { q: "Is this available only in Maharashtra?", a: "Our initial launch focus is Maharashtra. We know its records, portals, and registrar workflows deeply — that focus is what makes our reviews reliable." },
  { q: "What documents do I need to upload?", a: "Any property papers you already have — sale deeds, 7/12 extracts, tax records, allotment letters, layout papers, or related documents. Don't worry if some are missing; identifying gaps is part of the review." },
  { q: "How long does verification take?", a: "Typically 3–7 working days depending on the plan, property type, location, and record availability. Priority reviews are scoped per case." },
  { q: "Is the Land Rating a legal guarantee?", a: "No. It is a simplified expert summary based on reviewed information available at the time of review. It is not title insurance and not a guarantee of outcome." },
  { q: "Is this legal advice or a verification report?", a: "Terraa & Title provides a structured verification report and risk summary. It does not replace independent legal advice where required — though it gives your lawyer a much stronger starting point." },
  { q: "Can brokers and lawyers use this for multiple cases?", a: "Yes. Agents and legal professionals regularly use our reports across multiple client matters. Contact us for volume arrangements." },
];

export const GOVT_PORTALS = [
  { name: "MahaBhulekh", what: "7/12 & 8A extracts, property cards" },
  { name: "IGR Maharashtra e-Search", what: "Registered deeds & encumbrances" },
  { name: "Digital Satbara (DSLR)", what: "Digitally signed land record copies" },
  { name: "e-Ferfar", what: "Mutation entries & pending ferfars" },
  { name: "Bhunaksha Maharashtra", what: "Cadastral maps & plot boundaries" },
  { name: "MahaRERA", what: "Project & promoter registration" },
  { name: "eCourts / NJDG", what: "Civil & revenue litigation checks" },
  { name: "CERSAI", what: "Registered security interests & mortgages" },
  { name: "MCA21", what: "Company charge search for corporate sellers" },
  { name: "NGDRS / Sub-Registrar Offices", what: "Registration record retrieval" },
];

export const SERVICES = [
  {
    icon: FileSearch,
    title: "Title Search & Verification",
    short: "Comprehensive examination of ownership records to confirm a clear, marketable title.",
    long: "We trace the complete chain of ownership across decades of records, identifying every transfer, gift, inheritance, and partition. Our reports flag defects, breaks in chain, and competing claims before they become costly disputes.",
  },
  {
    icon: Landmark,
    title: "Land Records Retrieval",
    short: "Certified copies of 7/12 extracts, mutation entries, property cards, and revenue records.",
    long: "Our field teams retrieve certified documents directly from sub-registrar offices, revenue departments, and municipal records rooms — saving your team weeks of procedural delays.",
  },
  {
    icon: ScrollText,
    title: "Encumbrance Certificate Reports",
    short: "Detailed EC reports revealing mortgages, liens, charges, and registered claims.",
    long: "We compile and interpret encumbrance certificates over any period you require, presenting registered transactions, subsisting mortgages, and charges in a clear, decision-ready format.",
  },
  {
    icon: Scale,
    title: "Legal Due Diligence Support",
    short: "Structured diligence packs for lawyers, lenders, and acquisition teams.",
    long: "From litigation checks to development permissions and reservation status, we assemble the documentary foundation your legal opinion or credit decision depends on.",
  },
  {
    icon: ScanSearch,
    title: "Property Document Verification",
    short: "Authentication of sale deeds, agreements, POAs, and chain documents.",
    long: "We verify documents against registry records, confirm registration details, and detect inconsistencies, interpolations, and fraudulent instruments before you transact.",
  },
  {
    icon: History,
    title: "Ownership History Reports",
    short: "Thirty-year and full-chain ownership histories with genealogy mapping.",
    long: "Our analysts reconstruct ownership timelines with supporting documents at each link — essential for inheritance matters, boundary disputes, and institutional acquisitions.",
  },
];

export const INDUSTRIES = [
  {
    icon: Scale,
    title: "Law Firms",
    text: "Court-ready title reports and certified record retrieval that strengthen opinions, conveyancing, and litigation strategy.",
  },
  {
    icon: Building2,
    title: "Real Estate Developers",
    text: "Land aggregation diligence, chain verification, and encumbrance clearance across large parcels and multiple owners.",
  },
  {
    icon: PiggyBank,
    title: "Banks & Financial Institutions",
    text: "Collateral verification and title clearance reports aligned to credit policy timelines and audit standards.",
  },
  {
    icon: TrendingUp,
    title: "Property Investors",
    text: "Independent verification before capital commitment — ownership, encumbrances, litigation, and marketability.",
  },
  {
    icon: Gov,
    title: "Government Agencies",
    text: "Records reconciliation, acquisition support, and title certification for public projects and land pooling schemes.",
  },
  {
    icon: Home,
    title: "Individual Buyers",
    text: "Plain-language title reports that let families purchase their home or plot with complete confidence.",
  },
];

export const WHY_POINTS = [
  { icon: ShieldCheck, title: "Verified at the Source", text: "Every finding is verified against original registry and revenue records — never third-party databases alone." },
  { icon: History, title: "Decades of Records Expertise", text: "Our researchers read old deeds, vernacular records, and legacy registers that automated searches miss." },
  { icon: Scale, title: "Legally Structured Reports", text: "Reports are formatted for direct use in legal opinions, credit files, and board approvals." },
  { icon: FileSearch, title: "Defined Turnarounds", text: "Standard, priority, and urgent tracks with committed delivery dates — and proactive status updates." },
  { icon: ScanSearch, title: "Fraud Detection Focus", text: "Trained eyes for forged instruments, impersonation patterns, and suspicious chain breaks." },
  { icon: Landmark, title: "Pan-Regional Reach", text: "Retrieval network spanning sub-registrar offices, tehsils, and municipal records rooms across regions." },
];

export const PROCESS_STEPS = [
  { step: "01", title: "Submit Your Request", text: "Share the property details and documents through our title search request form." },
  { step: "02", title: "Records Retrieval", text: "Our field teams pull original records from registries, revenue offices, and courts." },
  { step: "03", title: "Expert Examination", text: "Analysts reconstruct the ownership chain and examine every encumbrance and claim." },
  { step: "04", title: "Decision-Ready Report", text: "You receive a clear, structured report with findings, red flags, and recommendations." },
];

export const TESTIMONIALS = [
  {
    quote: "Terraa & Title uncovered an unregistered family claim two other agencies missed. Their report saved our client from a seven-figure dispute.",
    name: "Adv. Rohan Mehta",
    role: "Partner, Mehta & Associates",
  },
  {
    quote: "For a 40-acre aggregation with over sixty owners, their chain verification was flawless. The diligence pack went straight to our board.",
    name: "Priya Deshmukh",
    role: "VP Land Acquisition, Skyline Developers",
  },
  {
    quote: "Their collateral verification reports fit our credit format exactly and always arrive within the committed timeline.",
    name: "Suresh Iyer",
    role: "Chief Credit Officer, Apex Housing Finance",
  },
  {
    quote: "As first-time buyers we understood every page of the report. They walked us through each finding personally.",
    name: "Neha & Arjun Kulkarni",
    role: "Homebuyers, Mumbai",
  },
];

export const FAQS = [
  {
    q: "What is a title search and why do I need one?",
    a: "A title search is a detailed examination of public records to confirm a property's legal ownership and uncover claims, liens, or encumbrances against it. It protects you from buying property with defects that could cost you ownership or money later.",
  },
  {
    q: "How long does a title search take?",
    a: "A standard search takes 7–10 working days. Priority (3–5 days) and urgent (24–72 hours) tracks are available depending on the jurisdiction and record availability. You choose your required turnaround when submitting a request.",
  },
  {
    q: "What documents do I need to provide?",
    a: "At minimum, the property address and survey/plot number. Copies of the sale deed, 7/12 extract, property card, or previous agreements help us move faster, and you can upload them directly with your request.",
  },
  {
    q: "How many years back do you search?",
    a: "Our standard reports cover 30 years of ownership history. Full-chain searches going back to the root of title are available for high-value transactions and litigation matters.",
  },
  {
    q: "Do you cover properties outside Maharashtra?",
    a: "Yes. Our retrieval network covers major regions, and we confirm coverage and timelines for your specific jurisdiction before accepting the assignment.",
  },
  {
    q: "Can your reports be used in court or for bank loans?",
    a: "Yes. Our reports are structured for direct use in legal opinions, credit files, and court proceedings, with certified copies of supporting records annexed where required.",
  },
  {
    q: "What if you find a problem with the title?",
    a: "Every red flag is documented with the underlying record and a clear explanation of its implication. Where possible, we outline the curative steps required to clear the defect.",
  },
  {
    q: "How is my information handled?",
    a: "All client information and uploaded documents are stored securely and shared only with the analysts assigned to your matter. We never disclose your inquiry to third parties.",
  },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1758612120966-b20c01160c7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBlc3RhdGUlMjBhZXJpYWx8ZW58MHx8fHwxNzg1MDk0NTM3fDA&ixlib=rb-4.1.0&q=85",
  services: "https://images.pexels.com/photos/9458996/pexels-photo-9458996.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  industries: "https://images.pexels.com/photos/37582309/pexels-photo-37582309.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  process: "https://images.pexels.com/photos/4134179/pexels-photo-4134179.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  office: "https://images.pexels.com/photos/13068366/pexels-photo-13068366.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  handshake: "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjByZWFsJTIwZXN0YXRlJTIwaGFuZHNoYWtlfGVufDB8fHx8MTc4NTA5NDU0NHww&ixlib=rb-4.1.0&q=85",
};
